import React, { useState, useEffect, useRef } from "react";
import {
  FaBriefcase,
  FaGraduationCap,
  FaHome,
  FaHandshake,
  FaArrowRight,
  FaStar,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";

import "../Styles/Home.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useInView } from "motion/react";
import Toast from "./Toast";
import HomeHero from "./HomeHero";
import ProcessStory from "./ProcessStory";
import DestinationGlobe from "./DestinationGlobe";

// 4-item Metric Counter Component
function AnimatedStatCounter({ target, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (!isInView) return;
    let startTime;
    const end = parseInt(target.replace(/,/g, ""), 10);
    if (isNaN(end)) return;

    const animate = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(ease * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, target, duration]);

  return (
    <span ref={ref} className="kmi-stat-number">
      {isInView ? count.toLocaleString() : "0"}{suffix}
    </span>
  );
}

// FAQs Data
const FAQS = [
  {
    q: "What does VE Abroad charge for initial consultation?",
    a: "Your initial profile evaluation and consultation is 100% free with zero obligation. Once you decide to partner with us, our service agreement is transparent with itemized milestones and no hidden fees."
  },
  {
    q: "Can I apply for overseas education without IELTS, GRE, or GMAT?",
    a: "Often, yes. Many partner universities in the UK, USA, Australia, and Europe accept Class XII English marks (70%+), Medium of Instruction (MOI) certificates, or internal university tests. We also provide IELTS coaching if you choose to take it."
  },
  {
    q: "How early should I begin my study abroad or PR application?",
    a: "For Study Abroad, starting 8 to 12 months before your target intake allows you to access early-bird scholarships and the best campus accommodation. For Canada PR or European Job Seeker, profile preparation and ECA credential evaluation takes 4–8 weeks."
  },
  {
    q: "Will you write my Statement of Purpose (SOP) for me?",
    a: "We work directly with you across dedicated review sittings. Admissions committees immediately detect generic agency templates — we refine your genuine narrative so your unique strengths shine through."
  },
  {
    q: "What assistance do you provide after my visa is approved?",
    a: "We help with student accommodation booking, foreign exchange remittances, discounted student airfares, health insurance, and pre-departure briefings. Our Mumbai team remains reachable even after you touch down."
  },
  {
    q: "Which countries do you specialize in for PR and Immigration?",
    a: "We are authorized specialists for Canada (Express Entry, PNP, LMIA), Australia (Subclass 189/190/491), Germany (EU Blue Card & Opportunity Card), UK Skilled Worker, and UAE Golden Visas."
  }
];

export default function Home() {
  const [toast, setToast] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(0);

  // The hero owns the assessment form and reports back through onToast.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <div className="kmi-home-page ve-scope">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <Helmet>
        <title>Overseas Education & Global Immigration Consultants in Mumbai | VE Abroad</title>
        <meta name="description" content="Personalised one-on-one overseas education & Canada PR counselling since 1989. University selection, SOP, visa filing, scholarships, education loans. Andheri West, Mumbai." />
        <link rel="canonical" href="https://veabroad.com/" />
        <meta property="og:title" content="Overseas Education & Global Immigration Consultants | VE Abroad" />
        <meta property="og:description" content="Personalised one-on-one overseas education & Canada PR counselling since 1989. Andheri West, Mumbai." />
        <meta property="og:url" content="https://veabroad.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* =========================================================================
          HERO
          ========================================================================= */}
      <HomeHero onToast={setToast} />

      {/* =========================================================================
          INFINITE MARQUEE TICKER
          ========================================================================= */}
      <div className="kmi-marquee-strip" aria-hidden="true">
        <div className="kmi-marquee-track">
          <div className="kmi-marquee-group">
            <span>Study Abroad</span>
            <span className="kmi-star">✦</span>
            <span>Canada PR Express Entry</span>
            <span className="kmi-star">✦</span>
            <span>Europe Job Seeker</span>
            <span className="kmi-star">✦</span>
            <span>UK Skilled Worker</span>
            <span className="kmi-star">✦</span>
            <span>500+ Partner Universities</span>
            <span className="kmi-star">✦</span>
            <span>Australia PR &amp; Study</span>
            <span className="kmi-star">✦</span>
            <span>IELTS &amp; PTE Coaching</span>
            <span className="kmi-star">✦</span>
            <span>Education Loans &amp; Forex</span>
            <span className="kmi-star">✦</span>
          </div>
          <div className="kmi-marquee-group">
            <span>Study Abroad</span>
            <span className="kmi-star">✦</span>
            <span>Canada PR Express Entry</span>
            <span className="kmi-star">✦</span>
            <span>Europe Job Seeker</span>
            <span className="kmi-star">✦</span>
            <span>UK Skilled Worker</span>
            <span className="kmi-star">✦</span>
            <span>500+ Partner Universities</span>
            <span className="kmi-star">✦</span>
            <span>Australia PR &amp; Study</span>
            <span className="kmi-star">✦</span>
            <span>IELTS &amp; PTE Coaching</span>
            <span className="kmi-star">✦</span>
            <span>Education Loans &amp; Forex</span>
            <span className="kmi-star">✦</span>
          </div>
        </div>
      </div>

      {/* =========================================================================
          DESTINATIONS — INTERACTIVE 3D GLOBE
          ========================================================================= */}
      <DestinationGlobe />

      {/* =========================================================================
          4-DIVIDED METRIC STAT ROW
          ========================================================================= */}
      <section className="kmi-stats-section">
        <div className="kmi-container">
          <div className="kmi-stats-grid">
            <div className="kmi-stat-card">
              <AnimatedStatCounter target="1,000" suffix="+" duration={1800} />
              <div className="kmi-stat-label">Successful Applications</div>
            </div>
            <div className="kmi-stat-card">
              <AnimatedStatCounter target="35" suffix="+" duration={1600} />
              <div className="kmi-stat-label">Years of Global Experience</div>
            </div>
            <div className="kmi-stat-card">
              <AnimatedStatCounter target="50" suffix="+" duration={1600} />
              <div className="kmi-stat-label">Countries Covered Worldwide</div>
            </div>
            <div className="kmi-stat-card">
              <AnimatedStatCounter target="98" suffix="%" duration={2000} />
              <div className="kmi-stat-label">Visa Approval Success Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          THE PROCESS, TOLD AS A STORY (sticky rail + six stages)
          ========================================================================= */}
      <ProcessStory />

      {/* =========================================================================
          CORE SERVICES BENTO GRID
          ========================================================================= */}
      <section className="kmi-section">
        <div className="kmi-container">
          <div className="kmi-section-header">
            <span className="kmi-section-tag">Comprehensive Pathways</span>
            <h2 className="kmi-section-title">Tailored Solutions for Your Global Future</h2>
            <p className="kmi-section-desc">
              Whether your objective is higher education, skilled overseas employment, or permanent residency, we provide complete, transparent support.
            </p>
          </div>

          <div className="kmi-services-bento">
            {/* Service 1: Immigration */}
            <Link to="/migrate" className="kmi-service-box">
              <div className="kmi-service-badge">Pathways</div>
              <div className="kmi-service-icon-wrap"><FaHome /></div>
              <h3>Immigration &amp; PR</h3>
              <p>Specialized permanent residency pathways for skilled professionals, investors, and families to Canada, Europe, and Australia.</p>
              <span className="kmi-service-link">Explore Immigration <FaArrowRight /></span>
            </Link>

            {/* Service 2: Study Overseas */}
            <Link to="/study" className="kmi-service-box">
              <div className="kmi-service-badge">Admissions</div>
              <div className="kmi-service-icon-wrap"><FaGraduationCap /></div>
              <h3>Study Overseas</h3>
              <p>University shortlisting, statement drafting, scholarship mapping, and direct applications to 500+ universities worldwide.</p>
              <span className="kmi-service-link">Explore Study Abroad <FaArrowRight /></span>
            </Link>

            {/* Service 3: Work Abroad */}
            <Link to="/work" className="kmi-service-box">
              <div className="kmi-service-badge">Careers</div>
              <div className="kmi-service-icon-wrap"><FaBriefcase /></div>
              <h3>Work Abroad</h3>
              <p>Job search support, CV restructuring to international formats, employer sponsorship, and Job Seeker visa guidance.</p>
              <span className="kmi-service-link">Explore Work Visas <FaArrowRight /></span>
            </Link>

            {/* Service 4: Visa & Coaching */}
            <Link to="/visa" className="kmi-service-box">
              <div className="kmi-service-badge">End-to-End</div>
              <div className="kmi-service-icon-wrap"><FaHandshake /></div>
              <h3>Visa &amp; Coaching</h3>
              <p>Impeccable visa filing, financial proofs auditing, mock interview prep, and certified IELTS Academic coaching.</p>
              <span className="kmi-service-link">Explore Visa Services <FaArrowRight /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================================================
          WHY CHOOSE VE ABROAD (TRUST PILLARS)
          ========================================================================= */}
      <section className="kmi-section">
        <div className="kmi-container">
          <div className="kmi-section-header">
            <span className="kmi-section-tag">Trust &amp; Transparency</span>
            <h2 className="kmi-section-title">Why Students &amp; Families Choose VE Abroad</h2>
          </div>

          <div className="kmi-trust-grid">
            <div className="kmi-trust-card">
              <div className="kmi-trust-icon-box">👨‍💼</div>
              <h3>Certified Advisors</h3>
              <p>ICCRC accredited consultants and ICEF certified education counselors with 10+ years of active advisory experience.</p>
            </div>

            <div className="kmi-trust-card">
              <div className="kmi-trust-icon-box">🎯</div>
              <h3>Unbiased Shortlisting</h3>
              <p>We recommend universities and immigration subclasses that actually align with your score, budget, and career goals.</p>
            </div>

            <div className="kmi-trust-card">
              <div className="kmi-trust-icon-box">📋</div>
              <h3>Transparent Fee Structure</h3>
              <p>Honest quotes in writing before work begins. No hidden commission markups, no unexpected fees later.</p>
            </div>

            <div className="kmi-trust-card">
              <div className="kmi-trust-icon-box">🤝</div>
              <h3>Lifelong Support</h3>
              <p>From initial profile check to post-landing forex, accommodation, and airport pickup — we stay by your side.</p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          TESTIMONIALS SECTION
          ========================================================================= */}
      <section className="kmi-section kmi-testimonials-bg">
        <div className="kmi-container">
          <div className="kmi-section-header">
            <span className="kmi-section-tag kmi-tag-light">Real Success Stories</span>
            <h2 className="kmi-section-title text-white">What Our Clients Say</h2>
          </div>

          <div className="kmi-testimonials-grid">
            <div className="kmi-testimonial-box">
              <div className="kmi-quote-sign">“</div>
              <div className="kmi-test-country">🍁 Canada PR</div>
              <p className="kmi-test-text">
                VE Abroad made my Canadian PR journey seamless. Their step-by-step document review and CRS optimization was exceptional. Received my ITA in just 4 months!
              </p>
              <div className="kmi-test-author">
                <div>
                  <h4>Sarah Sharma</h4>
                  <span>Software Engineer, Toronto</span>
                </div>
                <div className="kmi-test-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
              </div>
            </div>

            <div className="kmi-testimonial-box">
              <div className="kmi-quote-sign">“</div>
              <div className="kmi-test-country">🎓 Australia Study</div>
              <p className="kmi-test-text">
                Thanks to their overseas counseling, I got admission to the University of Melbourne with a merit scholarship. They also helped with my GTE statement and visa filing.
              </p>
              <div className="kmi-test-author">
                <div>
                  <h4>Raj Patel</h4>
                  <span>Masters Student, Melbourne</span>
                </div>
                <div className="kmi-test-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
              </div>
            </div>

            <div className="kmi-testimonial-box">
              <div className="kmi-quote-sign">“</div>
              <div className="kmi-test-country">🇬🇧 UK Work Visa</div>
              <p className="kmi-test-text">
                Professional, reliable, and results-driven. They helped me secure my UK Skilled Worker visa in under 6 months with zero complications. Highly recommended!
              </p>
              <div className="kmi-test-author">
                <div>
                  <h4>Hemant Kumar</h4>
                  <span>Marketing Manager, London</span>
                </div>
                <div className="kmi-test-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          FAQ ACCORDION SECTION
          ========================================================================= */}
      <section className="kmi-section kmi-bg-surface">
        <div className="kmi-container">
          <div className="kmi-section-header">
            <span className="kmi-section-tag">Frequently Asked Questions</span>
            <h2 className="kmi-section-title">Everything You Need to Know</h2>
            <p className="kmi-section-desc">
              Straightforward answers to the most common questions about overseas study, permanent residency, and visa processes.
            </p>
          </div>

          <div className="kmi-faq-wrapper">
            {FAQS.map((faq, index) => {
              const isOpen = expandedFaq === index;
              return (
                <div key={index} className={`kmi-faq-item ${isOpen ? 'open' : ''}`}>
                  <button 
                    className="kmi-faq-question"
                    onClick={() => setExpandedFaq(isOpen ? null : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    {isOpen ? <FaChevronUp className="kmi-faq-arrow" /> : <FaChevronDown className="kmi-faq-arrow" />}
                  </button>
                  {isOpen && (
                    <div className="kmi-faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* CTA Banner inside FAQ */}
          <div className="kmi-faq-cta-banner">
            <div>
              <h3>Have a specific question about your profile?</h3>
              <p>Speak directly with our senior consultant in Andheri West, Mumbai or book an online video call.</p>
            </div>
            <Link to="/consultationForm" className="kmi-btn-primary">
              Book a Free Consultation <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}